<template>
  <v-container
    class="text-center d-flex align-center justify-center"
    style="height: 100vh"
  >
    <div v-if="!isRanOnLocal && !isLocationEnabled" class="location-disabled-msg">
      <b>IMPORTANT! </b>{{ locationDisplayMsg }}
    </div>
    <div id="overlay" v-show="!isRanOnLocal && !isLocationEnabled" />
    <div>
      <div class="brand-logo">
        <img src="@/assets/sol.svg" />
        <span class="primaryText--text version-text">v{{ version }}</span>
      </div>
      <h3 class="primaryText--text py-2">Session limit reached</h3>
      <p class="primaryText--text pt-2 pb-1">
        As per our new security policy, you can only log in a maximum of 2 sessions at a
        time. Please log out of any extra sessions to continue this session.
      </p>

      <v-card dark pa-0 ma-0>
        <v-card-title class="title primary py-4">Session Details</v-card-title>
        <v-data-table
          :headers="headers"
          :items="Object.values(sessions)"
          class="session-table primary elevation-1"
          hide-actions
        >
          <template v-slot:headers="props">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.text"
                :class="[header.class, `text-xs-${header.align}`]"
                :style="{ width: header.width }"
              >
                {{ $t(header.text) }}
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <tr class="primaryText--text">
              <td>{{ props.item.device }}</td>
              <td>{{ props.item.os }}</td>
              <td>{{ props.item.browser }}</td>
              <td>{{ props.item.ip }}</td>
              <td>{{ props.item.login }}</td>
              <td>
                <v-dialog v-model="logoutDialog" max-width="450">
                  <template v-slot:activator="{ on }">
                    <a
                      v-on="on"
                      v-show="!props.item.isLoggedOut"
                      @click.prevent="setLogoutDialog(props.item.token)"
                      href="#"
                      class="link-text"
                    >
                      {{ $t('Logout Session') }}
                    </a>
                  </template>

                  <v-card class="primary primaryText--text pa-4">
                    <v-card-title class="headline">
                      {{ $t('Are you sure you want to logout?') }}
                    </v-card-title>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn flat color="error" @click="logoutDialog = false"
                        >{{ $t('Cancel') }}</v-btn
                      >
                      <v-btn
                        color="accent"
                        dark
                        @click="
                          logoutHandler(selectedToken);
                          logoutDialog = false;
                        "
                      >
                        {{ $t('Yes, Logout') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <span v-show="props.item.isLoggedOut">Logged Out</span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-btn
        :disabled="!isProceed"
        color="accent"
        class="ml-1 mt-4"
        @click.prevent="loginHandler"
        >Proceed</v-btn
      >
    </div>
  </v-container>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapGetters, mapActions, mapMutations } from "vuex";
import Login from "@/services/login";
import { getClientSideInfo } from "@/util/mixin/index.js";
import { version } from "../../../package.json";

export default {
  mixins: [getClientSideInfo],
  data() {
    return {
      otpText: "",
      version,
      headers: [
        {
          text: "Device",
          align: "left",
          value: "device",
          class: "header-style primaryText--text",
          sortable: false,
        },
        {
          text: "OS",
          align: "left",
          value: "os",
          class: "header-style primaryText--text",
          sortable: false,
        },
        {
          text: "Browser",
          align: "left",
          value: "browser",
          class: "header-style primaryText--text",
          sortable: false,
        },
        {
          text: "IP Address",
          align: "left",
          value: "ip",
          class: "header-style primaryText--text",
          sortable: false,
        },
        {
          text: "Last Login",
          align: "left",
          value: "login",
          class: "header-style primaryText--text",
          sortable: false,
        },
        {
          text: "",
          align: "left",
          value: "action",
          class: "primaryText--text",
          sortable: false,
        },
      ],
      sessions: {},
      email: "",
      password: "",
      selectedToken: null,
      logoutDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      "getUserInfo",
      "getGeneralState",
      "getRules"
    ]),
    isProceed() {
      return Object.values(this.sessions).some((el) => el.isLoggedOut);
    },
  },
  mounted() {
    const info = this.$route.params.info;
    this.otpText = this.$route.params.otp;
    this.addSessionData(info);
  },
  methods: {
    ...mapActions(["loginWithOtpAction"]),
    ...mapMutations([
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT",
      "SET_SESSIONS"
    ]),
    setLogoutDialog(token) {
      this.selectedToken = token;
      this.logoutDialog = true;
    },
    async logoutHandler(token) {
      if (this.sessions[token]) {
        this.$set(this.sessions[token], "isLoggedOut", true);
        this.SET_SESSIONS({ token, isLoggedOut: true });

        try {
          const payload = {
            token,
            userId: this.sessions[token].userId,
            email: this.sessions[token].email,
            info: await this.clientsInfo(),
          };
          await Login.logoutUser(payload);
        } catch (err) {
          console.error("Error logging out:", err);
        }
      }
    },
    updateSnackbar(payload) {
      this.SET_SHOW_SNACKBAR(true);
      this.SET_SNACKBAR_CONTENT(payload);
    },
    async loginHandler() {
      const userId = this.getUserInfo("userId");
      const payload = {
        userId,
        code: this.otpText,
        info: await this.clientsInfo(),
      };
      try {
        await this.loginWithOtpAction(payload);
        const diff = this.getGeneralState("passwordResetDiff");
        if (diff < 85) {
          if (this.getGeneralState("showHomeDB")) {
            this.$router.push(
              `${this.getRules(["rule", "access_config", "home", "path"])}`
            );
          } else {
            this.$router.push(
              `${this.getRules(["rule", "access_config", "plantOverview", "path"])}`
            );
          }
        } else {
          this.$router.push({
            name: "setPassword",
          });
        }
      } catch (err) {
        if (err.response && err.response.status === 409) {
          const loggedInSessions = err.response.data.data;
          const jsonString = JSON.stringify({
            data: loggedInSessions,
            email: this.model.username,
            password: this.model.password,
          });

          // Convert JSON string to Base64
          const info = Buffer.from(jsonString).toString("base64");
          this.$router.push({
            name: "sessionManagement",
            params: {
              info: info,
              otp: this.otpText,
            },
          });
        } else {
          this.updateSnackbar({
            icon: "error",
            timeout: 5000,
            header: "",
            message: err.response.data.message || "OTP verification failed",
          });
        }
      }
    },
    addSessionData(info) {
      const decodedInfo = JSON.parse(Buffer.from(info, "base64").toString("utf-8"));
      const deviceInfo = decodedInfo.data;
      this.email = decodedInfo.email;
      this.password = decodedInfo.password;

      let sessions = {};
      deviceInfo.forEach((el) => {
        const [
          deviceType,
          deviceModel,
          osName,
          osVersion,
          browserName,
        ] = el.device_type.split("||");
        const ip = el.ip_address;

        const device =
          /^\s*$/.test(deviceType) && /^\s*$/.test(deviceModel)
            ? "_"
            : deviceType.charAt(0).toUpperCase() +
              deviceType.slice(1) +
              ` ${deviceModel}`;
        const isLoggedOut = _.isEmpty(this.getGeneralState("sessions"))
          ? false
          : this.getGeneralState("sessions")[el.token];
        sessions[el.token] = {
          device,
          os:
            /^\s*$/.test(osName) && /^\s*$/.test(osVersion)
              ? "_"
              : `${osName} ${osVersion}`,
          browser: /^\s*$/.test(browserName) ? "_" : browserName,
          ip,
          login: moment.unix(el.logged_at).format("YYYY-MM-DD hh:mm:ss A"),
          token: el.token,
          email: decodedInfo.email,
          userId: el.user_id,
          isLoggedOut,
        };

        this.SET_SESSIONS({ token: el.token, isLoggedOut });
      });

      this.sessions = sessions;
    },
  },
  watch: {
    "$route.params.info": function (info) {
      // React to the route parameter change
      this.addSessionData(info);
    },
  },
};
</script>

<style scoped>
.brand-logo img {
  width: 150px;
  margin-top: 4px;
  margin-right: -12px;
}

.version-text {
  font-size: 14px;
  margin-left: 20px;
}

.header-style {
  padding-left: 24px !important;
}

.link-text {
  font-size: 14px;
  color: var(--v-activeText-base);
  cursor: pointer;
  text-decoration: none;
  position: relative;
}

.link-text::after {
  content: "";
  position: absolute;
  width: 0;
  height: 1px;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--v-activeText-base);
  transition: width 0.3s ease-in-out;
}

.link-text:hover::after {
  width: 100%;
}

#overlay {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 1;
  opacity: 0.8;
  background: #000;
}

.location-disabled-msg {
  position: absolute;
  top: 0;
  width: 100%;
  height: 4vh;
  z-index: 2;
  color: #000;
  font-size: 14px;
  text-align: center;
  line-height: 4vh;
  background: var(--v-accent-base);
}
</style>
